export const TENANT_APPEARANCE_COLOR_KEYS = [
  'brandPrimary', 'brandSecondary', 'brandAccent', 'brandWarm', 'brandHighlight',
  'logoGlowPrimary', 'logoGlowSecondary', 'logoGlowSurface',
  'landingSectionBackground', 'landingSectionAlternateBackground', 'landingCardBackground', 'landingCardBorder',
  'landingHeadingText', 'landingBodyText', 'landingAccentText', 'landingDecorativeText', 'landingDecorativePrimary', 'landingDecorativeSecondary',
  'backgroundPage', 'backgroundSurface', 'backgroundMuted', 'backgroundStrong',
  'backgroundRaised', 'backgroundOverlay',
  'textHeading', 'textBody', 'textMuted', 'textSoft', 'textLink', 'textInverse',
  'borderDefault', 'borderControl', 'borderFocus',
  'borderMuted', 'borderStrong', 'borderDivider',
  'actionPrimary', 'actionPrimaryText', 'actionDanger', 'actionDangerText',
  'actionSecondary', 'actionSecondaryText', 'actionSecondaryBorder',
  'actionPrimaryBorder', 'actionGhost', 'actionGhostText', 'actionGhostBorder', 'actionDangerBorder',
  'controlBackground', 'controlText', 'controlPlaceholder',
  'controlIcon', 'controlDisabledBackground', 'controlDisabledText', 'controlInvalidBorder',
  'navigationBackground', 'navigationText', 'navigationMutedText', 'navigationActiveBackground', 'navigationActiveText', 'navigationNestedActiveBackground', 'navigationNestedActiveText',
  'navigationBorder', 'navigationIcon', 'navigationHoverBackground', 'navigationTopbarBackground', 'navigationTopbarText',
  'dataHeaderBackground', 'dataRowBackground', 'dataAlternateRowBackground', 'dataRowHoverBackground', 'dataSelectedRowBackground', 'dataSelectedRowText',
  'overlayModalBackground', 'overlayPopoverBackground', 'overlayTooltipBackground', 'overlayTooltipText', 'overlayBackdrop',
  'feedbackSuccess', 'feedbackInfo', 'feedbackWarning', 'feedbackDanger',
  'feedbackSuccessBackground', 'feedbackWarningBackground', 'feedbackDangerBackground'
] as const

export type TenantAppearanceColorKey = typeof TENANT_APPEARANCE_COLOR_KEYS[number]
export type TenantAppearancePalette = Record<TenantAppearanceColorKey, string>

export const TENANT_APPEARANCE_FONT_WEIGHT_KEYS = ['body', 'heading', 'control', 'navigation'] as const
export type TenantAppearanceFontWeightKey = typeof TENANT_APPEARANCE_FONT_WEIGHT_KEYS[number]
export type TenantAppearanceTypography = Record<TenantAppearanceFontWeightKey, number>

export const TENANT_APPEARANCE_GRADIENT_KEYS = ['page', 'hero', 'action', 'navigation', 'landingSection', 'landingCard', 'landingDecoration'] as const
export type TenantAppearanceGradientKey = typeof TENANT_APPEARANCE_GRADIENT_KEYS[number]
export interface TenantAppearanceGradient { enabled: boolean; start: string; end: string; angle: number }
export type TenantAppearanceGradientSet = Record<TenantAppearanceGradientKey, TenantAppearanceGradient>
export type TenantAppearanceGradients = Record<'light' | 'dark', TenantAppearanceGradientSet>

export interface TenantAppearanceDocument {
  schemaVersion: 3
  light: TenantAppearancePalette
  dark: TenantAppearancePalette
  typography: TenantAppearanceTypography
  gradients: TenantAppearanceGradients
}

export interface PublishedTenantAppearanceView {
  config: TenantAppearanceDocument
  version: number
}

export interface AdminTenantAppearanceView {
  draft: TenantAppearanceDocument
  published: TenantAppearanceDocument
  version: number
  publishedAt: string | null
}

export interface TenantAppearanceContrastIssue {
  mode: 'light' | 'dark'
  foreground: TenantAppearanceColorKey
  background: TenantAppearanceColorKey | `gradients.${TenantAppearanceGradientKey}.${'start' | 'end'}`
  ratio: number
}

const CSS_VARIABLE_BY_KEY: Record<TenantAppearanceColorKey, string[]> = {
  brandPrimary: ['--tq-color-navy'],
  brandSecondary: ['--tq-color-teal'],
  brandAccent: ['--tq-color-magenta'],
  brandWarm: ['--tq-color-orange'],
  brandHighlight: ['--tq-color-yellow', '--tq-required-marker'],
  logoGlowPrimary: ['--tq-logo-glow-primary'],
  logoGlowSecondary: ['--tq-logo-glow-secondary'],
  logoGlowSurface: ['--tq-logo-glow-surface'],
  landingSectionBackground: ['--tq-landing-section-bg'],
  landingSectionAlternateBackground: ['--tq-landing-section-alt-bg'],
  landingCardBackground: ['--tq-landing-card-bg'],
  landingCardBorder: ['--tq-landing-card-border'],
  landingHeadingText: ['--tq-landing-heading'],
  landingBodyText: ['--tq-landing-text'],
  landingAccentText: ['--tq-landing-accent-text'],
  landingDecorativeText: ['--tq-landing-decorative-text'],
  landingDecorativePrimary: ['--tq-landing-decoration-primary'],
  landingDecorativeSecondary: ['--tq-landing-decoration-secondary'],
  backgroundPage: ['--tq-bg'],
  backgroundSurface: ['--tq-bg-elevated', '--tq-card-bg', '--tq-modal-bg'],
  backgroundMuted: ['--tq-bg-muted', '--tq-table-row-bg'],
  backgroundStrong: ['--tq-bg-strong', '--tq-table-header-bg'],
  backgroundRaised: ['--tq-bg-raised'],
  backgroundOverlay: ['--tq-bg-overlay'],
  textHeading: ['--tq-text-heading'],
  textBody: ['--tq-text'],
  textMuted: ['--tq-text-muted'],
  textSoft: ['--tq-text-soft'],
  textLink: ['--tq-link'],
  textInverse: ['--tq-on-strong-action'],
  borderDefault: ['--tq-border', '--tq-card-border'],
  borderControl: ['--tq-control-border'],
  borderFocus: ['--tq-focus', '--tq-control-active-border'],
  borderMuted: ['--tq-border-muted'],
  borderStrong: ['--tq-border-strong'],
  borderDivider: ['--tq-border-divider'],
  actionPrimary: ['--tq-action'],
  actionPrimaryText: ['--tq-on-action'],
  actionSecondary: ['--tq-secondary-action'],
  actionSecondaryText: ['--tq-on-secondary-action'],
  actionSecondaryBorder: ['--tq-secondary-action-border'],
  actionDanger: ['--tq-danger'],
  actionDangerText: ['--tq-on-danger'],
  actionPrimaryBorder: ['--tq-action-border'],
  actionGhost: ['--tq-ghost-action'],
  actionGhostText: ['--tq-on-ghost-action'],
  actionGhostBorder: ['--tq-ghost-action-border'],
  actionDangerBorder: ['--tq-danger-border'],
  controlBackground: ['--tq-control-bg'],
  controlText: ['--tq-control-text'],
  controlPlaceholder: ['--tq-control-placeholder'],
  controlIcon: ['--tq-control-icon'],
  controlDisabledBackground: ['--tq-control-disabled-bg'],
  controlDisabledText: ['--tq-control-disabled-text'],
  controlInvalidBorder: ['--tq-control-invalid-border'],
  navigationBackground: ['--tq-nav-bg'],
  navigationText: ['--tq-nav-text'],
  navigationMutedText: ['--tq-nav-text-muted'],
  navigationActiveBackground: ['--tq-nav-active-bg'],
  navigationActiveText: ['--tq-nav-active-text'],
  navigationNestedActiveBackground: ['--tq-nav-nested-active-bg'],
  navigationNestedActiveText: ['--tq-nav-nested-active-text'],
  navigationBorder: ['--tq-nav-border'],
  navigationIcon: ['--tq-nav-icon'],
  navigationHoverBackground: ['--tq-nav-hover-bg'],
  navigationTopbarBackground: ['--tq-topbar-bg'],
  navigationTopbarText: ['--tq-topbar-text'],
  dataHeaderBackground: ['--tq-table-header-bg'],
  dataRowBackground: ['--tq-table-row-bg'],
  dataAlternateRowBackground: ['--tq-table-row-alt-bg'],
  dataRowHoverBackground: ['--tq-table-row-hover-bg'],
  dataSelectedRowBackground: ['--tq-table-row-selected-bg'],
  dataSelectedRowText: ['--tq-table-row-selected-text'],
  overlayModalBackground: ['--tq-modal-bg'],
  overlayPopoverBackground: ['--tq-popover-bg'],
  overlayTooltipBackground: ['--tq-tooltip-bg'],
  overlayTooltipText: ['--tq-tooltip-text'],
  overlayBackdrop: ['--tq-backdrop-bg'],
  feedbackSuccess: ['--tq-success', '--tq-success-readable'],
  feedbackInfo: ['--tq-info-readable'],
  feedbackWarning: ['--tq-warning-action', '--tq-orange-readable'],
  feedbackDanger: ['--tq-danger-readable'],
  feedbackSuccessBackground: ['--tq-success-bg'],
  feedbackWarningBackground: ['--tq-warning-bg'],
  feedbackDangerBackground: ['--tq-danger-bg']
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i
const LEGACY_COLOR_DEFAULTS = {
  light: {
    logoGlowPrimary: '#e6b800', logoGlowSecondary: '#087eaa', logoGlowSurface: '#fffdf0',
    landingSectionBackground: '#eef2fb', landingSectionAlternateBackground: '#e4eaf7', landingCardBackground: '#ffffff', landingCardBorder: '#9eadd0',
    landingHeadingText: '#101d50', landingBodyText: '#53617f', landingAccentText: '#066587', landingDecorativeText: '#101d50', landingDecorativePrimary: '#087eaa', landingDecorativeSecondary: '#315bea',
    actionSecondary: '#fffdf8', actionSecondaryText: '#1d1c2b', actionSecondaryBorder: '#c8c2b7',
    backgroundRaised: '#ffffff', backgroundOverlay: '#f7f9ff', borderMuted: '#e4eaf7', borderStrong: '#9eadd0', borderDivider: '#cbd5e8',
    actionPrimaryBorder: '#173b9b', actionGhost: '#eef2fb', actionGhostText: '#173b9b', actionGhostBorder: '#cbd5e8', actionDangerBorder: '#8f1728',
    controlIcon: '#53617f', controlDisabledBackground: '#e4eaf7', controlDisabledText: '#53617f', controlInvalidBorder: '#b42335',
    navigationNestedActiveBackground: '#233b7a', navigationNestedActiveText: '#ffffff', navigationBorder: '#29365f', navigationIcon: '#aeb9d8', navigationHoverBackground: '#14265c', navigationTopbarBackground: '#ffffff', navigationTopbarText: '#17213d',
    dataHeaderBackground: '#dce6fa', dataRowBackground: '#ffffff', dataAlternateRowBackground: '#e4eaf7', dataRowHoverBackground: '#dce6fa', dataSelectedRowBackground: '#cbd9ff', dataSelectedRowText: '#101d50',
    overlayModalBackground: '#ffffff', overlayPopoverBackground: '#ffffff', overlayTooltipBackground: '#101d50', overlayTooltipText: '#ffffff', overlayBackdrop: '#17213d',
    feedbackInfo: '#183da8', feedbackSuccessBackground: '#e3f4eb', feedbackWarningBackground: '#fff1d6', feedbackDangerBackground: '#fde8eb'
  },
  dark: {
    logoGlowPrimary: '#e6c84a', logoGlowSecondary: '#22d3ee', logoGlowSurface: '#eef6ff',
    landingSectionBackground: '#101735', landingSectionAlternateBackground: '#172044', landingCardBackground: '#101735', landingCardBorder: '#29365f',
    landingHeadingText: '#ffffff', landingBodyText: '#aeb9d8', landingAccentText: '#12b8c4', landingDecorativeText: '#7290ff', landingDecorativePrimary: '#12b8c4', landingDecorativeSecondary: '#7290ff',
    actionSecondary: '#292943', actionSecondaryText: '#f6f4ed', actionSecondaryBorder: '#53536f',
    backgroundRaised: '#172044', backgroundOverlay: '#101735', borderMuted: '#29365f', borderStrong: '#4b5c8d', borderDivider: '#29365f',
    actionPrimaryBorder: '#7290ff', actionGhost: '#172044', actionGhostText: '#edf2ff', actionGhostBorder: '#3b4a78', actionDangerBorder: '#ef7180',
    controlIcon: '#aeb9d8', controlDisabledBackground: '#172044', controlDisabledText: '#8794b8', controlInvalidBorder: '#ef7180',
    navigationNestedActiveBackground: '#233b7a', navigationNestedActiveText: '#ffffff', navigationBorder: '#29365f', navigationIcon: '#aeb9d8', navigationHoverBackground: '#14265c', navigationTopbarBackground: '#101735', navigationTopbarText: '#edf2ff',
    dataHeaderBackground: '#1c2b5e', dataRowBackground: '#101735', dataAlternateRowBackground: '#172044', dataRowHoverBackground: '#1c2b5e', dataSelectedRowBackground: '#233b7a', dataSelectedRowText: '#ffffff',
    overlayModalBackground: '#101735', overlayPopoverBackground: '#172044', overlayTooltipBackground: '#edf2ff', overlayTooltipText: '#101735', overlayBackdrop: '#080d27',
    feedbackInfo: '#7edfe5', feedbackSuccessBackground: '#143a2d', feedbackWarningBackground: '#493116', feedbackDangerBackground: '#4a2029'
  }
} as const
const DEFAULT_TYPOGRAPHY: TenantAppearanceTypography = { body: 400, heading: 600, control: 500, navigation: 400 }
const ALLOWED_FONT_WEIGHTS = [300, 400, 500, 600, 700]
const DEFAULT_GRADIENTS: TenantAppearanceGradients = {
  light: {
    page: { enabled: true, start: '#eef2fb', end: '#dce6fa', angle: 145 },
    hero: { enabled: true, start: '#14226f', end: '#087eaa', angle: 135 },
    action: { enabled: true, start: '#173b9b', end: '#315bea', angle: 110 },
    navigation: { enabled: true, start: '#090f2c', end: '#14265c', angle: 180 },
    landingSection: { enabled: true, start: '#eef2fb', end: '#dce6fa', angle: 145 },
    landingCard: { enabled: false, start: '#ffffff', end: '#e4eaf7', angle: 135 },
    landingDecoration: { enabled: true, start: '#087eaa', end: '#315bea', angle: 135 }
  },
  dark: {
    page: { enabled: true, start: '#080d27', end: '#142052', angle: 145 },
    hero: { enabled: true, start: '#14226f', end: '#087eaa', angle: 135 },
    action: { enabled: true, start: '#173b9b', end: '#315bea', angle: 110 },
    navigation: { enabled: true, start: '#090f2c', end: '#14265c', angle: 180 },
    landingSection: { enabled: true, start: '#101735', end: '#172044', angle: 145 },
    landingCard: { enabled: true, start: '#101735', end: '#14265c', angle: 135 },
    landingDecoration: { enabled: true, start: '#12b8c4', end: '#7290ff', angle: 135 }
  }
}

const CONTRAST_PAIRS = [
  ['textBody', 'backgroundPage'],
  ['textHeading', 'backgroundPage'],
  ['textMuted', 'backgroundPage'],
  ['controlText', 'controlBackground'],
  ['actionPrimaryText', 'actionPrimary'],
  ['actionSecondaryText', 'actionSecondary'],
  ['actionDangerText', 'actionDanger'],
  ['navigationText', 'navigationBackground'],
  ['navigationActiveText', 'navigationActiveBackground'],
  ['navigationNestedActiveText', 'navigationNestedActiveBackground'],

  ['textBody', 'backgroundSurface'],
  ['textHeading', 'backgroundSurface'],
  ['textMuted', 'backgroundSurface'],
  ['textSoft', 'backgroundSurface'],
  ['textBody', 'backgroundMuted'],
  ['textHeading', 'backgroundMuted'],
  ['textMuted', 'backgroundMuted'],
  ['textSoft', 'backgroundMuted'],
  ['textBody', 'backgroundStrong'],
  ['textHeading', 'backgroundStrong'],
  ['textMuted', 'backgroundStrong'],
  ['textSoft', 'backgroundStrong'],
  ['textBody', 'backgroundRaised'],
  ['textHeading', 'backgroundRaised'],
  ['textMuted', 'backgroundRaised'],
  ['textSoft', 'backgroundRaised'],
  ['textBody', 'backgroundOverlay'],
  ['textHeading', 'backgroundOverlay'],
  ['textMuted', 'backgroundOverlay'],
  ['textSoft', 'backgroundOverlay'],
  ['textBody', 'overlayModalBackground'],
  ['textHeading', 'overlayModalBackground'],
  ['textMuted', 'overlayModalBackground'],
  ['textSoft', 'overlayModalBackground'],
  ['textBody', 'overlayPopoverBackground'],
  ['textHeading', 'overlayPopoverBackground'],
  ['textMuted', 'overlayPopoverBackground'],
  ['textSoft', 'overlayPopoverBackground'],
  ['textBody', 'dataRowBackground'],
  ['textHeading', 'dataRowBackground'],
  ['textMuted', 'dataRowBackground'],
  ['textSoft', 'dataRowBackground'],
  ['textBody', 'dataAlternateRowBackground'],
  ['textHeading', 'dataAlternateRowBackground'],
  ['textMuted', 'dataAlternateRowBackground'],
  ['textSoft', 'dataAlternateRowBackground'],
  ['textBody', 'dataRowHoverBackground'],
  ['textHeading', 'dataRowHoverBackground'],
  ['textMuted', 'dataRowHoverBackground'],
  ['textSoft', 'dataRowHoverBackground'],
  ['controlPlaceholder', 'controlBackground'],
  ['controlIcon', 'controlBackground'],
  ['controlDisabledText', 'controlDisabledBackground'],
  ['dataSelectedRowText', 'dataSelectedRowBackground'],
  ['overlayTooltipText', 'overlayTooltipBackground'],
  ['navigationTopbarText', 'navigationTopbarBackground'],
  ['navigationMutedText', 'navigationBackground'],
  ['navigationText', 'navigationHoverBackground'],
  ['feedbackSuccess', 'feedbackSuccessBackground'],
  ['feedbackWarning', 'feedbackWarningBackground'],
  ['feedbackDanger', 'feedbackDangerBackground'],
  ['landingBodyText', 'landingSectionBackground'],
  ['landingBodyText', 'landingSectionAlternateBackground'],
  ['landingHeadingText', 'landingCardBackground'],
  ['landingAccentText', 'landingSectionBackground'],
] as const satisfies ReadonlyArray<readonly [TenantAppearanceColorKey, TenantAppearanceColorKey]>

function relativeLuminance(color: string): number {
  const channels = [1, 3, 5].map(offset => Number.parseInt(color.slice(offset, offset + 2), 16) / 255)
  const [red = 0, green = 0, blue = 0] = channels.map(channel => (
    channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  ))
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export function appearanceContrastRatio(foreground: string, background: string): number {
  const foregroundLuminance = relativeLuminance(foreground)
  const backgroundLuminance = relativeLuminance(background)
  return (Math.max(foregroundLuminance, backgroundLuminance) + 0.05) / (Math.min(foregroundLuminance, backgroundLuminance) + 0.05)
}

export function tenantAppearanceContrastIssues(document: TenantAppearanceDocument): TenantAppearanceContrastIssue[] {
  const issues: TenantAppearanceContrastIssue[] = []
  for (const mode of ['light', 'dark'] as const) {
    for (const [foreground, background] of CONTRAST_PAIRS) {
      const ratio = appearanceContrastRatio(document[mode][foreground], document[mode][background])
      if (ratio < 4.5) issues.push({ mode, foreground, background, ratio: Number(ratio.toFixed(2)) })
    }
    const gradientPairs = [
      ['actionPrimaryText', 'action'],
      ['navigationText', 'navigation'],
      ['textInverse', 'hero'],
      ['textBody', 'page']
    ] as const satisfies ReadonlyArray<readonly [TenantAppearanceColorKey, TenantAppearanceGradientKey]>
    for (const [foreground, gradientKey] of gradientPairs) {
      const gradient = document.gradients[mode][gradientKey]
      if (!gradient.enabled) continue
      for (const endpoint of ['start', 'end'] as const) {
        const ratio = appearanceContrastRatio(document[mode][foreground], gradient[endpoint])
        if (ratio < 4.5) issues.push({ mode, foreground, background: `gradients.${gradientKey}.${endpoint}`, ratio: Number(ratio.toFixed(2)) })
      }
    }
  }
  return issues
}

export function normalizeAppearanceDocument(value: unknown): TenantAppearanceDocument | null {
  if (!value || typeof value !== 'object') return null
  const source = value as Record<string, unknown>
  if (source.schemaVersion !== 1 && source.schemaVersion !== 2 && source.schemaVersion !== 3) return null
  const normalizePalette = (input: unknown, mode: 'light' | 'dark'): TenantAppearancePalette | null => {
    if (!input || typeof input !== 'object') return null
    const palette = input as Record<string, unknown>
    const entries = TENANT_APPEARANCE_COLOR_KEYS.map((key) => [key, palette[key] ?? LEGACY_COLOR_DEFAULTS[mode][key as keyof typeof LEGACY_COLOR_DEFAULTS.light]])
    if (entries.some(([, color]) => typeof color !== 'string' || !HEX_COLOR.test(color))) return null
    return Object.fromEntries(entries) as TenantAppearancePalette
  }
  const light = normalizePalette(source.light, 'light')
  const dark = normalizePalette(source.dark, 'dark')
  const typographySource = source.typography && typeof source.typography === 'object'
    ? source.typography as Record<string, unknown>
    : DEFAULT_TYPOGRAPHY
  const typographyEntries = TENANT_APPEARANCE_FONT_WEIGHT_KEYS.map(key => [key, typographySource[key] ?? DEFAULT_TYPOGRAPHY[key]] as const)
  if (typographyEntries.some(([, weight]) => typeof weight !== 'number' || !ALLOWED_FONT_WEIGHTS.includes(weight))) return null
  const typography = Object.fromEntries(typographyEntries) as TenantAppearanceTypography
  const gradientSource = source.schemaVersion === 3 && source.gradients && typeof source.gradients === 'object'
    ? source.gradients as Record<string, unknown>
    : {}
  let invalidGradient = false
  const gradients = Object.fromEntries((['light', 'dark'] as const).map((mode) => {
    const modeSource = gradientSource[mode] && typeof gradientSource[mode] === 'object'
      ? gradientSource[mode] as Record<string, unknown>
      : {}
    const entries: Array<[TenantAppearanceGradientKey, TenantAppearanceGradient]> = []
    for (const key of TENANT_APPEARANCE_GRADIENT_KEYS) {
      const candidate = modeSource[key] && typeof modeSource[key] === 'object'
        ? modeSource[key] as Record<string, unknown>
        : DEFAULT_GRADIENTS[mode][key]
      if (typeof candidate.enabled !== 'boolean' || typeof candidate.start !== 'string' || !HEX_COLOR.test(candidate.start) || typeof candidate.end !== 'string' || !HEX_COLOR.test(candidate.end) || !Number.isInteger(candidate.angle) || Number(candidate.angle) < 0 || Number(candidate.angle) > 360) {
        invalidGradient = true
        continue
      }
      entries.push([key, { enabled: candidate.enabled, start: candidate.start, end: candidate.end, angle: Number(candidate.angle) }])
    }
    const set = Object.fromEntries(entries) as TenantAppearanceGradientSet
    return [mode, set]
  })) as TenantAppearanceGradients
  if (invalidGradient) return null
  return light && dark ? { schemaVersion: 3, light, dark, typography, gradients } : null
}

export function appearanceCssVariables(palette: TenantAppearancePalette, typography?: TenantAppearanceTypography, gradients?: TenantAppearanceGradientSet): Record<string, string> {
  return Object.fromEntries([
    ...TENANT_APPEARANCE_COLOR_KEYS.flatMap(key => (
    CSS_VARIABLE_BY_KEY[key].map(variable => [variable, palette[key]])
    )),
    // Rebind Nuxt aliases at the same scope as the palette. Inherited aliases
    // otherwise resolve against the published root instead of a draft preview.
    ...Object.entries({
      '--ui-text': palette.textBody,
      '--ui-text-toned': `color-mix(in oklch, ${palette.textBody} 72%, ${palette.textMuted})`,
      '--ui-text-muted': palette.textMuted,
      '--ui-text-dimmed': palette.textSoft,
      '--ui-text-highlighted': palette.textHeading,
      '--ui-text-inverted': palette.actionPrimaryText,
      '--ui-bg': palette.backgroundPage,
      '--ui-bg-muted': palette.backgroundMuted,
      '--ui-bg-elevated': palette.backgroundRaised,
      '--ui-bg-accented': palette.backgroundStrong,
      '--ui-bg-inverted': palette.actionPrimary,
      '--ui-border': palette.borderDefault,
      '--ui-border-muted': palette.borderMuted,
      '--ui-border-accented': palette.borderStrong,
      '--ui-border-inverted': palette.actionPrimaryBorder,
      '--tq-table-header-text': appearanceContrastRatio(palette.textHeading, palette.dataHeaderBackground) >= appearanceContrastRatio(palette.textInverse, palette.dataHeaderBackground) ? palette.textHeading : palette.textInverse,
      '--tq-required-marker': palette.feedbackDanger,
      '--tq-teal-readable': palette.feedbackInfo,
      '--tq-accent-readable': palette.textLink,
      '--ui-primary': palette.actionPrimary,
      '--ui-secondary': `color-mix(in oklch, ${palette.brandSecondary} 64%, ${palette.textBody})`,
      '--ui-success': palette.feedbackSuccess,
      '--ui-info': palette.feedbackInfo,
      '--ui-warning': palette.feedbackWarning,
      '--ui-error': palette.feedbackDanger
    }),
    ...typography
      ? [
          ['--tq-font-weight-regular', String(typography.body)],
          ['--tq-font-weight-heading', String(typography.heading)],
          ['--tq-font-weight-bold', String(typography.heading)],
          ['--tq-font-weight-control', String(typography.control)],
          ['--tq-font-weight-navigation', String(typography.navigation)]
        ]
      : []
    , ...gradients
      ? TENANT_APPEARANCE_GRADIENT_KEYS.map((key) => {
          const gradient = gradients[key]
          const cssKey = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)
          return [`--tq-gradient-${cssKey}`, gradient?.enabled ? `linear-gradient(${gradient.angle}deg, ${gradient.start}, ${gradient.end})` : 'none']
        })
      : []
  ])
}

function cssDeclaration(palette: TenantAppearancePalette, typography: TenantAppearanceTypography, gradients: TenantAppearanceGradientSet) {
  return Object.entries(appearanceCssVariables(palette, typography, gradients))
    .map(([variable, value]) => `${variable}:${value}`)
    .join(';')
}

export function tenantAppearanceCss(document: TenantAppearanceDocument | null) {
  if (!document) return ''
  // Tenant tokens must win over the base :root/.light/.dark declarations even
  // when the framework appends its stylesheet after this runtime style tag.
  return `html:root,html.light{${cssDeclaration(document.light, document.typography, document.gradients.light)}}html.dark{${cssDeclaration(document.dark, document.typography, document.gradients.dark)}}`
}
